// =====================================================================
// Tracking de eventos / conversiones — NULED OS
//
// Dispara eventos a 2 destinos:
//   1. Nuestra tabla marketing_events (source of truth propia)
//   2. window.gtag (Google Ads / GA4 — cuando GTAG_ID esté configurado)
//
// Eventos clave (definidos en STANDARD_EVENTS):
//   lead_distribuidor  — form /distribuidor enviado
//   click_whatsapp     — click en cualquier botón WhatsApp
//   lead_portal        — primer login de un distribuidor
//   quote_created      — cotización generada
//   view_pricing       — distribuidor logueado ve precios mayoristas
//   search             — búsqueda en el sitio (metadata.q) — alimenta el CRM
//
// Para activar Google Ads tracking, agregar VITE_GTAG_ID al .env
// (formato: G-XXXXXXXXXX o AW-XXXXXXXXXX).
// =====================================================================

import { supabase } from './supabase';

// La etiqueta es un identificador público. El fallback evita que un deploy sin
// variable VITE_GTAG_ID deje de medir silenciosamente.
const GTAG_ID = (import.meta.env.VITE_GTAG_ID as string | undefined) || 'AW-16577158214';
// Pixel NULED Web (dataset 1060595653577761). Override con VITE_META_PIXEL_ID si se requiere.
const META_PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined) || '1060595653577761';

// Eventos estándar — solo estos cuentan como conversiones.
// `meta` mapea al evento estándar de Meta Pixel (para optimización + retargeting).
export const STANDARD_EVENTS = {
  lead_distribuidor: { value_mxn: 500,   gtag: 'lead_distribuidor',  meta: 'Lead',                 googleAdsSendTo: 'AW-16577158214/a9DQCI-RnvAcEMa4zeA9' },
  click_whatsapp:    { value_mxn: 100,   gtag: 'click_whatsapp',     meta: 'Contact',              googleAdsSendTo: 'AW-16577158214/16woCNOFofAcEMa4zeA9' },
  lead_portal:       { value_mxn: 1500,  gtag: 'lead_portal',        meta: 'CompleteRegistration', googleAdsSendTo: null },
  quote_created:     { value_mxn: 3000,  gtag: 'quote_created',      meta: 'Lead',                 googleAdsSendTo: null },
  view_pricing:      { value_mxn: 50,    gtag: 'view_pricing',       meta: 'ViewContent',          googleAdsSendTo: null },
} as const;

export type StandardEvent = keyof typeof STANDARD_EVENTS;

declare global {
  interface Window {
    // gtag opcional — solo existe si GTAG_ID está configurado
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    // fbq opcional — Meta Pixel, solo existe si META_PIXEL_ID está configurado
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const ATTRIBUTION_STORAGE_KEY = 'nuled_attr_v2';
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export type Attribution = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  landing_page: string | null;
  captured_at: string | null;
};

type StoredAttribution = Attribution & { expires_at: number };

export type TrackingOptions = {
  customerId?: string | null;
  userId?: string | null;
  distributorApplicationId?: string | null;
  dealId?: string | null;
  dedupeKey?: string | null;
  persist?: boolean;
};

// === Identidad del usuario con sesión (la fija auth.tsx) ===
// Así toda la actividad de una cuenta en el portal (vistas, búsquedas,
// cotizaciones) queda ligada a su cliente y aparece en el CRM.
const trackingIdentity: { userId: string | null; customerId: string | null } = { userId: null, customerId: null };
export function setTrackingIdentity(userId: string | null, customerId: string | null): void {
  trackingIdentity.userId = userId;
  trackingIdentity.customerId = customerId;
}

// === Session ID en localStorage (anónimo, persiste entre páginas) ===
export function getTrackingSessionId(): string {
  try {
    let sid = localStorage.getItem('nuled_sid');
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('nuled_sid', sid);
    }
    return sid;
  } catch {
    return `s_anon_${Date.now()}`;
  }
}

function emptyAttribution(): Attribution {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    gclid: null,
    gbraid: null,
    wbraid: null,
    fbclid: null,
    landing_page: null,
    captured_at: null,
  };
}

// === UTM + click IDs desde URL (atribución no-directa persistente por 90 días) ===
export function getAttribution(): Attribution {
  try {
    const params = new URLSearchParams(window.location.search);
    const captured: Attribution = {
      utm_source:   params.get('utm_source'),
      utm_medium:   params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term:     params.get('utm_term'),
      utm_content:  params.get('utm_content'),
      gclid:        params.get('gclid'),
      gbraid:       params.get('gbraid'),
      wbraid:       params.get('wbraid'),
      fbclid:       params.get('fbclid'),
      landing_page: `${window.location.origin}${window.location.pathname}`,
      captured_at: new Date().toISOString(),
    };

    const hasCampaignSignal = [
      captured.utm_source,
      captured.utm_medium,
      captured.utm_campaign,
      captured.utm_term,
      captured.utm_content,
      captured.gclid,
      captured.gbraid,
      captured.wbraid,
      captured.fbclid,
    ].some(Boolean);

    // Last non-direct touch: una visita directa posterior no borra la campaña de origen.
    if (hasCampaignSignal) {
      const stored: StoredAttribution = {
        ...captured,
        expires_at: Date.now() + ATTRIBUTION_TTL_MS,
      };
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(stored));
      return captured;
    }

    const raw = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return emptyAttribution();
    const stored = JSON.parse(raw) as StoredAttribution;
    if (!stored.expires_at || stored.expires_at <= Date.now()) {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return emptyAttribution();
    }
    const { expires_at: _expiresAt, ...attribution } = stored;
    return attribution;
  } catch {
    return emptyAttribution();
  }
}

// === Disparar evento ===
export async function trackEvent(
  eventName: StandardEvent | string,
  metadata: Record<string, unknown> = {},
  valueOverrideMxn?: number,
  options: TrackingOptions = {},
): Promise<void> {
  const standard = (STANDARD_EVENTS as Record<string, {
    value_mxn: number;
    gtag: string;
    meta: string;
    googleAdsSendTo: string | null;
  }>)[eventName];
  const valueMxn = valueOverrideMxn ?? standard?.value_mxn ?? null;

  const attribution = getAttribution();
  let duplicate = false;

  // === 1) Insert en nuestra tabla marketing_events (anon RLS permite) ===
  if (options.persist !== false) try {
    const { error } = await supabase.from('marketing_events').insert({
      event_name: eventName,
      event_value_mxn: valueMxn,
      session_id: getTrackingSessionId(),
      user_id: options.userId ?? trackingIdentity.userId,
      customer_id: options.customerId ?? trackingIdentity.customerId,
      distributor_application_id: options.distributorApplicationId ?? null,
      deal_id: options.dealId ?? null,
      dedupe_key: options.dedupeKey ?? null,
      page_url: typeof window !== 'undefined' ? window.location.href : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_term: attribution.utm_term,
      utm_content: attribution.utm_content,
      gclid: attribution.gclid,
      gbraid: attribution.gbraid,
      wbraid: attribution.wbraid,
      fbclid: attribution.fbclid,
      metadata,
    });
    if (error) {
      if (error.code === '23505' && options.dedupeKey) duplicate = true;
      else console.warn('[tracking] failed to log event', eventName, error);
    }
  } catch (e) {
    // No bloqueamos UX si el insert falla
    console.warn('[tracking] failed to log event', eventName, e);
  }

  // === 2) Forward a gtag si está cargado ===
  if (!duplicate && typeof window !== 'undefined' && window.gtag && standard) {
    try {
      window.gtag('event', standard.gtag, {
        value: valueMxn,
        currency: 'MXN',
        ...metadata,
      });
      if (standard.googleAdsSendTo) {
        window.gtag('event', 'conversion', {
          send_to: standard.googleAdsSendTo,
          value: valueMxn,
          currency: 'MXN',
        });
      }
    } catch (e) {
      console.warn('[tracking] gtag dispatch failed', e);
    }
  }

  // === 3) Forward a Meta Pixel (fbq) si está cargado ===
  if (!duplicate && typeof window !== 'undefined' && window.fbq && standard) {
    try {
      window.fbq('track', standard.meta, {
        value: valueMxn ?? undefined,
        currency: 'MXN',
        ...metadata,
      });
    } catch (e) {
      console.warn('[tracking] fbq dispatch failed', e);
    }
  }
}

// === Inicializar gtag si VITE_GTAG_ID está set ===
// Llamar UNA VEZ en main.tsx. Si no hay ID, no hace nada.
export function initGtag(): void {
  if (!GTAG_ID || typeof window === 'undefined') return;
  if (window.gtag) return; // ya cargado

  // Cargar script de Google
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GTAG_ID, {
    send_page_view: true,
  });

  // Capturar UTM y guardar
  getAttribution();
}

// === Inicializar Meta Pixel si VITE_META_PIXEL_ID está set ===
// Llamar UNA VEZ en main.tsx. Si no hay ID, no hace nada. Dispara PageView.
export function initMetaPixel(): void {
  if (!META_PIXEL_ID || typeof window === 'undefined') return;
  if (window.fbq) return; // ya cargado

  // Meta Pixel base code (adaptado a TS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const n: any = function fbq() {
    // eslint-disable-next-line prefer-rest-params
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
  window.fbq = n;
  if (!window._fbq) window._fbq = n;

  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(s);

  n('init', META_PIXEL_ID);
  n('track', 'PageView');
}

// === Helper: ViewContent en página de producto (para retargeting de catálogo) ===
// content_ids debe ser el SKU para que empate con el feed del catálogo Meta.
export function trackProductView(sku: string, name?: string): void {
  if (typeof window !== 'undefined' && window.fbq) {
    try {
      window.fbq('track', 'ViewContent', {
        content_ids: [sku],
        content_type: 'product',
        content_name: name,
      });
    } catch (e) {
      console.warn('[tracking] fbq ViewContent failed', e);
    }
  }
  void trackEvent('view_content', { sku, name });
}

// === Helper: link a WhatsApp que dispara tracking automáticamente ===
// Reemplaza <a href="wa.me/..."> por <WhatsAppLink phone="..." text="..." />
export function buildWhatsAppHref(phone: string, text: string): string {
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
}
